import { useState, useEffect } from 'react';
import { Plus, Save, X, Pencil, Trash2 } from 'lucide-react';
import { fetchApi } from '../../services/api';
import type { IntercompanyGLMapping } from '../../types/cashCockpit';
import { GROUP_COMPANIES } from '../../types/cashCockpit';

interface Props {
    canEdit: boolean;
}

type DraftMapping = {
    companyCode: string;
    glAccountFrom: string;
    glAccountTo: string;
    counterpartyCompanyCode: string;
    direction: 'Receivable' | 'Payable' | 'Auto';
    description: string;
};

const EMPTY_DRAFT: DraftMapping = {
    companyCode: 'bin',
    glAccountFrom: '',
    glAccountTo: '',
    counterpartyCompanyCode: '',
    direction: 'Auto',
    description: '',
};

export default function IntercompanyMappingPanel({ canEdit }: Props) {
    const [mappings, setMappings] = useState<IntercompanyGLMapping[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [draft, setDraft] = useState<DraftMapping | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editBuf, setEditBuf] = useState<DraftMapping | null>(null);

    const load = async () => {
        setLoading(true);
        try {
            const rows = await fetchApi<IntercompanyGLMapping[]>('/cash-cockpit/intercompany-mapping');
            setMappings(rows);
            setError(null);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load mappings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { void load(); }, []);

    const onSaveNew = async () => {
        if (!draft) return;
        if (!draft.glAccountFrom.trim() || !draft.glAccountTo.trim()) {
            setError('G/L account range is required');
            return;
        }
        try {
            await fetchApi('/cash-cockpit/intercompany-mapping', {
                method: 'POST',
                body: JSON.stringify({
                    companyCode: draft.companyCode,
                    glAccountFrom: draft.glAccountFrom.trim(),
                    glAccountTo: draft.glAccountTo.trim(),
                    counterpartyCompanyCode: draft.counterpartyCompanyCode.trim() || null,
                    direction: draft.direction,
                    description: draft.description.trim() || null,
                }),
            });
            setDraft(null);
            setError(null);
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Save failed');
        }
    };

    const onStartEdit = (m: IntercompanyGLMapping) => {
        setEditingId(m.id);
        setEditBuf({
            companyCode: m.companyCode,
            glAccountFrom: m.glAccountFrom,
            glAccountTo: m.glAccountTo,
            counterpartyCompanyCode: m.counterpartyCompanyCode ?? '',
            direction: m.direction,
            description: m.description ?? '',
        });
    };

    const onSaveEdit = async () => {
        if (editingId == null || !editBuf) return;
        try {
            await fetchApi(`/cash-cockpit/intercompany-mapping/${editingId}`, {
                method: 'PUT',
                body: JSON.stringify({
                    companyCode: editBuf.companyCode,
                    glAccountFrom: editBuf.glAccountFrom,
                    glAccountTo: editBuf.glAccountTo,
                    counterpartyCompanyCode: editBuf.counterpartyCompanyCode.trim() || null,
                    direction: editBuf.direction,
                    description: editBuf.description,
                }),
            });
            setEditingId(null);
            setEditBuf(null);
            setError(null);
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Update failed');
        }
    };

    const onDelete = async (id: number) => {
        try {
            await fetchApi(`/cash-cockpit/intercompany-mapping/${id}`, { method: 'DELETE' });
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Delete failed');
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-900">Intercompany G/L Mapping</h3>
                {canEdit && !draft && (
                    <button
                        onClick={() => setDraft({ ...EMPTY_DRAFT })}
                        className="inline-flex items-center gap-1 text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        <Plus className="w-4 h-4" /> New Mapping
                    </button>
                )}
            </div>
            <p className="text-xs text-gray-500 mb-3">
                Map BC G/L account ranges to intercompany loan direction, per company.
                The Intercompany tab pulls live balances from DataWarehouse.BalanceSheetBalancesByMonth
                for accounts within these ranges.
            </p>

            {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-3">{error}</div>
            )}

            {draft && (
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 mb-3 grid grid-cols-6 gap-2 items-end">
                    <LabeledSelect label="Company *" value={draft.companyCode}
                        onChange={v => setDraft({ ...draft, companyCode: v })}
                        options={GROUP_COMPANIES} />
                    <LabeledInput label="G/L From *" value={draft.glAccountFrom}
                        onChange={v => setDraft({ ...draft, glAccountFrom: v })} placeholder="1460" />
                    <LabeledInput label="G/L To *" value={draft.glAccountTo}
                        onChange={v => setDraft({ ...draft, glAccountTo: v })} placeholder="1499" />
                    <LabeledSelect label="Counterparty" value={draft.counterpartyCompanyCode}
                        onChange={v => setDraft({ ...draft, counterpartyCompanyCode: v })}
                        options={['', ...GROUP_COMPANIES.filter(c => c !== draft.companyCode)]} />
                    <LabeledSelect label="Direction *" value={draft.direction}
                        onChange={v => setDraft({ ...draft, direction: v as DraftMapping['direction'] })}
                        options={['Auto', 'Receivable', 'Payable']} />
                    <div className="col-span-6 flex items-end gap-2">
                        <LabeledInput label="Description" value={draft.description}
                            onChange={v => setDraft({ ...draft, description: v })}
                            placeholder="e.g. Darlehen an Tochtergesellschaften" className="flex-1" />
                        <button onClick={onSaveNew} className="text-green-600 hover:text-green-800"><Save className="w-4 h-4" /></button>
                        <button onClick={() => setDraft(null)} className="text-gray-500 hover:text-gray-700"><X className="w-4 h-4" /></button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-center py-4 text-gray-500 text-xs">Loading mappings…</div>
            ) : mappings.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-xs">
                    No intercompany G/L mappings configured yet.
                </div>
            ) : (
                <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-gray-600">
                        <tr>
                            <th className="px-3 py-2 text-left">Company</th>
                            <th className="px-3 py-2 text-left">G/L Range</th>
                            <th className="px-3 py-2 text-left">Counterparty</th>
                            <th className="px-3 py-2 text-left">Direction</th>
                            <th className="px-3 py-2 text-left">Description</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            {canEdit && <th className="px-3 py-2 text-right">Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {mappings.map(m => {
                            const isEditing = editingId === m.id;
                            return (
                                <tr key={m.id} className="border-t border-gray-100">
                                    {isEditing && editBuf ? (
                                        <>
                                            <td className="px-3 py-1">
                                                <select value={editBuf.companyCode}
                                                    onChange={e => setEditBuf({ ...editBuf, companyCode: e.target.value })}
                                                    className="border border-gray-300 rounded px-1 py-0.5 uppercase">
                                                    {GROUP_COMPANIES.map(c => <option key={c} value={c}>{c}</option>)}
                                                </select>
                                            </td>
                                            <td className="px-3 py-1">
                                                <input value={editBuf.glAccountFrom} onChange={e => setEditBuf({ ...editBuf, glAccountFrom: e.target.value })}
                                                    className="w-16 border border-gray-300 rounded px-1 py-0.5 font-mono" />
                                                {' – '}
                                                <input value={editBuf.glAccountTo} onChange={e => setEditBuf({ ...editBuf, glAccountTo: e.target.value })}
                                                    className="w-16 border border-gray-300 rounded px-1 py-0.5 font-mono" />
                                            </td>
                                            <td className="px-3 py-1">
                                                <select value={editBuf.counterpartyCompanyCode}
                                                    onChange={e => setEditBuf({ ...editBuf, counterpartyCompanyCode: e.target.value })}
                                                    className="border border-gray-300 rounded px-1 py-0.5 uppercase">
                                                    <option value="">—</option>
                                                    {GROUP_COMPANIES.filter(c => c !== editBuf.companyCode).map(c => <option key={c} value={c}>{c}</option>)}
                                                </select>
                                            </td>
                                            <td className="px-3 py-1">
                                                <select value={editBuf.direction}
                                                    onChange={e => setEditBuf({ ...editBuf, direction: e.target.value as DraftMapping['direction'] })}
                                                    className="border border-gray-300 rounded px-1 py-0.5">
                                                    <option value="Auto">Auto</option>
                                                    <option value="Receivable">Receivable</option>
                                                    <option value="Payable">Payable</option>
                                                </select>
                                            </td>
                                            <td className="px-3 py-1">
                                                <input value={editBuf.description} onChange={e => setEditBuf({ ...editBuf, description: e.target.value })}
                                                    className="w-full border border-gray-300 rounded px-1 py-0.5" />
                                            </td>
                                            <td className="px-3 py-1 text-gray-500">—</td>
                                            <td className="px-3 py-1 text-right whitespace-nowrap">
                                                <button onClick={onSaveEdit} className="text-green-600 hover:text-green-800 mr-2"><Save className="w-3 h-3 inline" /></button>
                                                <button onClick={() => { setEditingId(null); setEditBuf(null); }} className="text-gray-500"><X className="w-3 h-3 inline" /></button>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td className="px-3 py-1 font-semibold uppercase">{m.companyCode}</td>
                                            <td className="px-3 py-1 font-mono">{m.glAccountFrom} – {m.glAccountTo}</td>
                                            <td className="px-3 py-1 font-semibold uppercase">{m.counterpartyCompanyCode ?? '—'}</td>
                                            <td className="px-3 py-1">{m.direction}</td>
                                            <td className="px-3 py-1 text-gray-600">{m.description ?? '—'}</td>
                                            <td className="px-3 py-1">
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${m.isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                                                    {m.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            {canEdit && (
                                                <td className="px-3 py-1 text-right whitespace-nowrap">
                                                    <button onClick={() => onStartEdit(m)} className="text-blue-600 hover:text-blue-800 mr-2"><Pencil className="w-3 h-3 inline" /></button>
                                                    <button onClick={() => onDelete(m.id)} className="text-red-600 hover:text-red-800"><Trash2 className="w-3 h-3 inline" /></button>
                                                </td>
                                            )}
                                        </>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function LabeledInput({ label, value, onChange, placeholder, className }: {
    label: string; value: string; onChange: (v: string) => void; placeholder?: string; className?: string;
}) {
    return (
        <label className={`block ${className ?? ''}`}>
            <span className="block text-[10px] uppercase text-gray-600">{label}</span>
            <input type="text" value={value} onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
        </label>
    );
}

function LabeledSelect({ label, value, onChange, options }: {
    label: string; value: string; onChange: (v: string) => void; options: readonly string[];
}) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase text-gray-600">{label}</span>
            <select value={value} onChange={e => onChange(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm uppercase">
                {options.map(opt => <option key={opt} value={opt}>{opt || '—'}</option>)}
            </select>
        </label>
    );
}
